"use client";

import { useTranslations } from "next-intl";
import Image from "next/image";
import { CSSProperties, useContext } from "react";
import SwiperCore from "swiper";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { FreeMode, Navigation, Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

import Button, { ButtonSize, ButtonTheme, ButtonVariant } from "@/components/ui/Button/Button";
import Icon from "@/components/ui/Icon/Icon";
import { ModalsContext } from "@/context/ModalsContext";
import { brochureDowloadCtaClick } from "@/dataLayer/dataLayer";
import useMediaQuery from "@/hooks/useMediaQuery";
import { BaseEntry, CustomLink, Media } from "@/types";

import { CardCarouselBottomNavigation } from "../AutogeneratedCarousel/CardCarouselBottomNavigation";
import CardGallery from "./CardGallery";
import "./CardsGalleriesCarousel.css";

SwiperCore.use([Pagination, Navigation]);

export interface GalleryCardEntry extends BaseEntry {
  title?: string;
  description?: string;
  textFont?: "El Messiri" | "Roboto";
  textColor?: string;
  backgroundColor?: string;
  textAlignment?: "top" | "bottom";
  image?: Media;
  video?: Media;
  videoBackupImage?: Media;
  aspectRatio: "3:4" | "4:3";
  gradient?: boolean;
  titleColor?: string;
  type: "galleryCardImageVideo" | "galleryCardText" | "galleryCardImageText";
}
export interface CardsGalleriesCarouselEntry extends BaseEntry {
  overtitle?: string;
  logo?: Media;
  brochure?: Media;
  cards: GalleryCardEntry[];
  textColor?: string;
  backgroundColor?: string;
}
interface Props {
  entry: CardsGalleriesCarouselEntry;
  projectInfo?: {
    title?: string;
    name?: string;
    url?: string;
  };
}

export default function CardsGalleriesCarousel({ entry, projectInfo }: Props) {
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const { setDownloadBrochureModalOpen, additionalInfo, settings } = useContext(ModalsContext);
  const t = useTranslations();

  return (
    <div
      className="flex flex-col w-full md:flex-row md:items-center overflow-hidden"
      style={
        {
          ...(entry.backgroundColor && { backgroundColor: entry.backgroundColor }),
          ...(entry.textColor && { color: entry.textColor, "--font-primary-color": entry.textColor }),
        } as CSSProperties
      }
    >
      <div className="block md:hidden ">
        {(entry.overtitle || entry.logo || entry.brochure) && (
          <div className="flex flex-row w-22\/24 mx-auto mt-xxl md:mt-0 md:w-[250px] md:flex-col md:items-center md:justify-center md:h-full">
            {(entry.overtitle || entry.logo) && (
              <div className="w-1/2 md:w-full text-center">
                {entry.logo && (
                  <Image
                    className="max-w-[120px] md:max-w-[auto] md:inline-block mx-auto"
                    src={entry.logo.url}
                    alt={entry.logo.title}
                    width={entry.logo.width}
                    height={entry.logo.height}
                  />
                )}
              </div>
            )}
            {entry.brochure && (
              <div className="w-1/2 text-right md:mt-l md:text-center md:w-full flex items-center justify-center">
                <Button
                  size={ButtonSize.SMALL}
                  theme={ButtonTheme.BLACK}
                  type="button"
                  variant={ButtonVariant.SECONDARY}
                  onClick={() => {
                    brochureDowloadCtaClick({ cta_name: t("label.brochure"), ...additionalInfo });
                    settings.brochureData = { url: entry.brochure?.url, projectInfo };
                    setDownloadBrochureModalOpen(true);
                  }}
                >
                  <span className="flex items-center uppercase gap-xs font-medium">
                    <Icon className="stroke-black" name="download" size="20" />
                    {t("label.brochure")}
                  </span>
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
      <div className="w-full my-xl md:w-24\/24 cardgallery-swiper">
        <div className={`h-CardGallery md:ml-[calc(${!(entry.overtitle || entry.logo || entry.brochure) && "100%_*_2_/_24"})]`}>
          <Swiper
            slidesPerView={"auto"}
            spaceBetween={isDesktop ? 16 : 8}
            navigation={{
              nextEl: `.swiper-button-next-${entry.id}`,
              prevEl: `.swiper-button-prev-${entry.id}`,
            }}
            pagination={{ type: "progressbar", el: `.swiper-pagination-carousel-${entry.id}` }}
            watchSlidesProgress={true}
            slidesOffsetAfter={isDesktop ? 100 : 16}
            freeMode={{ enabled: !isDesktop }}
            modules={[Navigation, FreeMode]}
          >
            {isDesktop && (entry.overtitle || entry.logo || entry.brochure) && (
              <SwiperSlide className="!w-fit flex flex-col justify-center !h-[630px] custom-slide">
                <div className="flex flex-row w-22\/24 mx-auto mt-xxl md:mt-0 md:w-[250px] md:flex-col md:items-center md:justify-center md:h-full">
                  {(entry.overtitle || entry.logo) && (
                    <div className="w-1/2 text-left md:text-center md:w-full">
                      {entry.overtitle && <div className="hidden md:block md:uppercase font-primary text-s font-medium">{entry.overtitle}</div>}
                      {entry.logo && (
                        <Image
                          className="md:inline-block"
                          src={entry.logo.url}
                          alt={entry.logo.title}
                          width={entry.logo.width}
                          height={entry.logo.height}
                        />
                      )}
                    </div>
                  )}
                  {entry.brochure && (
                    <div className="w-1/2 text-right md:mt-l md:text-center md:w-full flex items-center justify-center">
                      <Button
                        size={ButtonSize.MEDIUM}
                        customClass="border !py-[11px]"
                        theme={ButtonTheme.BLACK}
                        type="button"
                        variant={ButtonVariant.SECONDARY}
                        onClick={() => {
                          brochureDowloadCtaClick({ cta_name: t("label.brochure"), ...additionalInfo });
                          settings.brochureData = { url: entry.brochure?.url, projectInfo };
                          setDownloadBrochureModalOpen(true);
                        }}
                      >
                        <span className="flex items-center uppercase gap-xs font-medium">
                          <Icon className="stroke-inherit" name="download" size="20" />
                          {t("label.brochure")}
                        </span>
                      </Button>
                    </div>
                  )}
                </div>
              </SwiperSlide>
            )}
            {entry.cards.map((item, index) => (
              <SwiperSlide className={`!w-fit ${!isDesktop && "custom-slide"}`} key={index}>
                <CardGallery entry={item} />
              </SwiperSlide>
            ))}
          </Swiper>
        </div>

        <CardCarouselBottomNavigation carouselId={entry.id} customClass="mt-xl" carouselType={entry.type} />
      </div>
    </div>
  );
}
